/**
 * Category hues (`--nxt-hue-*`, static tokens in @blpde/nx-tokens base/hues.css).
 *
 * A hue tells categories apart — a request type, a board column, an AI hint —
 * where the intent roles (danger, success, …) would claim a meaning the thing
 * does not have. Use an intent tone when the colour says something, a hue when
 * it only groups.
 */
export type NxHue = 'violet' | 'teal' | 'sky' | 'rose' | 'lime' | 'amber' | 'slate' | 'emerald';

export const NX_HUES: readonly NxHue[] = [
  'violet',
  'teal',
  'sky',
  'rose',
  'lime',
  'amber',
  'slate',
  'emerald',
];

/**
 * Local custom properties pointing a component at one hue: `<prefix>` (solid),
 * `<prefix>-on`, `<prefix>-fg`, `<prefix>-subtle-bg`, `<prefix>-border`. Bind
 * the result as `:style` and read the properties in the component's CSS.
 */
export function hueVars(hue: NxHue | null | undefined, prefix = '--hue'): Record<string, string> {
  if (!hue) return {};
  const t = `--nxt-hue-${hue}`;
  return {
    [prefix]: `var(${t})`,
    [`${prefix}-on`]: `var(${t}-on)`,
    [`${prefix}-fg`]: `var(${t}-fg)`,
    [`${prefix}-subtle-bg`]: `var(${t}-subtle-bg)`,
    [`${prefix}-border`]: `var(${t}-border)`,
  };
}

/**
 * The hues `hueFor` picks from: every hue but the grey `slate`, which reads as
 * „disabled“, and `emerald` (I-2066), which came later — the pick is an index
 * into this list, so a new entry would move the colour of every existing key.
 */
const KEYED_HUES = NX_HUES.filter((hue) => hue !== 'slate' && hue !== 'emerald');

/**
 * A stable hue for a key — a name, an id: the same key always gets the same
 * hue, different keys spread over the colourful hues. For things that only
 * need telling apart (the avatar of a profile, a tag without a colour of its
 * own). Case and surrounding spaces do not count.
 */
export function hueFor(key: string): NxHue {
  const text = key.trim().toLocaleLowerCase();
  // FNV-1a: cheap, and small changes to the key move the hue.
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return KEYED_HUES[(hash >>> 0) % KEYED_HUES.length]!;
}
